import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PageHero from "@/components/landing/PageHero";
import SuiteCrossLink from "@/components/landing/SuiteCrossLink";
import { useSEO } from "@/hooks/useSEO";
import {
  Globe,
  Radar,
  Bell,
  TrendingUp,
  BotMessageSquare,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";

const authorities: { label: string; region: string }[] = [
  { label: "FDA", region: "United States" },
  { label: "EMA", region: "European Union" },
  { label: "MHRA", region: "United Kingdom" },
  { label: "Health Canada", region: "Canada" },
  { label: "PMDA", region: "Japan" },
  { label: "WHO", region: "Global" },
  { label: "SFDA", region: "Saudi Arabia" },
];

const capabilities: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: Radar,
    title: "Scheduled multi-source monitoring",
    body: "Scheduled collection of published warning letters, Form 483 observations, recalls, guidance and enforcement notices from seven authorities: FDA, EMA, MHRA, Health Canada, PMDA, WHO and SFDA. Coverage depends on what each authority publishes.",
  },
  {
    icon: Bell,
    title: "Filtered alerts that matter",
    body: "Filter alerts by authority, document type, topic and date so reviewers see the items relevant to their scope.",
  },
  {
    icon: TrendingUp,
    title: "Enforcement trends",
    body: "See which topics recur in published citations by year, authority and theme to help prioritise internal audit focus.",
  },
  {
    icon: BotMessageSquare,
    title: "Native AI Agents, governed inside Spine",
    body: "Audit-trailed GxP assistants cite sources and permitted Spine records for impact assessment. They propose and cite, never decide; authorized people approve all quality decisions.",
  },
];

const RegulatoryHubMarketing = () => {

  useSEO({
    title: "Regulatory Watch — Native in Pharmalify Spine",
    description:
      "Regulatory Watch is native in Pharmalify Spine: monitor regulatory sources and route reviewed alerts into Change Control, QRM and the SOP lifecycle.",
    keywords:
      "pharma regulatory intelligence, FDA warning letter monitoring, EMA inspection trends, MHRA alerts, Pharmalify Spine Regulatory Watch, compliance intelligence",
    canonical: "https://www.pharmalify.com/regulatory-hub",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Regulatory Watch — native in Pharmalify Spine",
      description:
         "Regulatory Watch is native in Pharmalify Spine: monitor regulatory sources and route reviewed alerts into Change Control, QRM and the SOP lifecycle.",
      applicationCategory: "BusinessApplication",
      applicationSubCategory: "Regulatory Intelligence & Monitoring",
      operatingSystem: "Web Browser",
      url: "https://www.pharmalify.com/regulatory-hub",
      isPartOf: { "@type": "SoftwareApplication", name: "Pharmalify Spine", url: "https://www.pharmalify.com/spine" },
    },
  });

  return (
    <main className="min-h-screen bg-background">
      <PageHero
        title="Regulatory Watch — native in Pharmalify Spine"
         description="Turn regulatory signals into controlled action inside Spine. Reviewed alerts feed Change Control, Quality Risk Management (QRM) and the SOP lifecycle with a traceable link from source to controlled change."
      />

      {/* Try-it strip */}
      <section className="bg-muted/30 border-b border-border/60">
        <div className="container py-6 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-6xl">
          <p className="text-sm text-muted-foreground">
            Regulatory Watch is an integrated Spine capability, not a standalone service. Access is provisioned under contract for your biopharma organization.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2">
            <Link to="/contact">Request Walkthrough <ArrowRight className="w-4 h-4" /></Link>
          </Button>
        </div>
      </section>

      {/* Authorities */}
      <section className="py-16 md:py-20">
        <div className="container max-w-6xl">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
              7+ Authorities Monitored
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
               A single evidence base for global regulatory change
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {authorities.map((a) => (
              <div key={a.label} className="flex flex-col items-center gap-1.5 p-4 rounded-lg bg-card border border-border/60">
                <Globe className="w-4 h-4 text-accent" />
                <span className="text-sm font-semibold text-foreground">{a.label}</span>
                <span className="text-[10px] text-muted-foreground text-center leading-tight">{a.region}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container max-w-6xl">
          <div className="grid md:grid-cols-2 gap-5">
            {capabilities.map((c) => (
              <Card key={c.title} className="border-border/60">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">{c.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Connected suite story */}
      <section className="py-16 md:py-20">
        <div className="container max-w-5xl">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-accent mb-3 px-3 py-1 rounded-full bg-accent/10">
               Connected to Spine workflows
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
               From regulatory alert to controlled quality action
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Radar,
                 title: "Change Control",
                 body: "Assess cited alerts and initiate Change Control when approved. Link the source, impact assessment, accountable owners and verification evidence.",
                href: "/spine",
              },
              {
                icon: BotMessageSquare,
                 title: "Quality Risk Management (QRM)",
                 body: "Feed reviewed regulatory signals into risk assessments. Native AI Agents can propose cited findings; qualified teams determine risk, controls and follow-up.",
                href: "/ai-agents",
              },
              {
                icon: ShieldCheck,
                 title: "SOP lifecycle",
                 body: "Connect approved changes to SOP revision, review, approval, effective dates and training, retaining traceability from the regulatory source to controlled evidence.",
                href: "/spine",
              },
            ].map((step) => (
              <div key={step.title} className="rounded-2xl border border-border/60 bg-card p-6">
                <step.icon className="w-7 h-7 text-accent mb-3" />
                <h3 className="font-semibold text-foreground mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">{step.body}</p>
                {step.href && (
                  <Link to={step.href} className="text-sm font-semibold text-accent hover:underline inline-flex items-center gap-1">
                    Learn more <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container max-w-4xl">
          <div className="relative overflow-hidden rounded-2xl bg-primary border border-accent/20 px-8 py-12 md:px-14 md:py-14 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-3">See Regulatory Watch inside Spine</h2>
             <p className="text-primary-foreground/70 max-w-2xl mx-auto mb-7">
               Explore how your biopharma team can assess regulatory impact and manage controlled follow-through inside Spine. Access is contract-based only.
            </p>
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2">
              <Link to="/contact">Request Walkthrough <ArrowRight className="w-4 h-4" /></Link>
            </Button>
            <div className="mt-4">
              <Link to="/ai-agents" className="text-sm text-primary-foreground/60 hover:text-primary-foreground inline-flex items-center gap-1">
                 <CheckCircle2 className="w-3.5 h-3.5" /> See how governed AI supports impact assessment
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SuiteCrossLink current="hub" />
    </main>
  );
};

export default RegulatoryHubMarketing;
